import React, { useState, useMemo, useEffect } from 'react';
import { Trophy, Medal, Mic, Play, Activity, Crown, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Entry {
  id: string;
  user_id: string;
  tournament_id: string;
  performance_id: string | null;
  status: 'registered' | 'submitted' | 'evaluated';
  score: number | null;
  created_at: string;
  updated_at: string | null;
  profile?: {
    username: string;
    full_name: string | null;
    avatar_url: string | null;
  };
  performance?: {
    id: string;
    vocal_recording_url: string;
    score: number | null;
    created_at: string;
    recording?: {
      id: string;
      tournament_round: number | null;
      song?: {
        id: string;
        title: string;
        artist: string;
        cover_url: string | null;
      };
    };
  };
}

interface RoundPerformance {
  id: string;
  user_id: string;
  tournament_round: number;
  song_id: string;
  created_at: string;
  song?: { id: string; title: string; artist: string; cover_url: string | null } | null;
  performance?: { id: string; score: number | null; vocal_recording_url: string; created_at: string; analysis?: { overall_score: number | null }[] | null }[] | null;
  profile?: { username: string; full_name: string | null; avatar_url: string | null } | null;
}

function getPerformanceScore(rp: RoundPerformance): number | null {
  const perf = Array.isArray(rp.performance) ? rp.performance[0] : null;
  if (!perf) return null;
  const analysisScore = Array.isArray(perf.analysis) && perf.analysis[0]?.overall_score != null
    ? Math.round(perf.analysis[0].overall_score)
    : null;
  return analysisScore ?? perf.score ?? null;
}

interface TournamentLeaderboardProps {
  entries: Entry[];
  currentUserId?: string;
  onPlayPerformance?: (entry: Entry) => void;
  primaryColor?: string;
  secondaryColor?: string;
  isSharedView?: boolean;
  totalRounds?: number;
  roundPerformances?: RoundPerformance[];
}

const TournamentLeaderboard: React.FC<TournamentLeaderboardProps> = ({
  entries,
  currentUserId,
  onPlayPerformance,
  primaryColor,
  secondaryColor,
  isSharedView = false,
  totalRounds = 0,
  roundPerformances = []
}) => {
  const accentColor = primaryColor || '#f97316';
  const bgColor = secondaryColor || '#581c87';
  const [selectedRound, setSelectedRound] = useState<'total' | number>('total');

  const sortedEntries = useMemo(() => [...entries]
    .filter(entry => entry.performance_id)
    .sort((a, b) => {
      if (a.score === null && b.score === null) return 0;
      if (a.score === null) return 1;
      if (b.score === null) return -1;
      return b.score - a.score;
    }), [entries]);

  const availableRounds = useMemo(() => {
    const rounds = new Set<number>();
    roundPerformances.forEach(rp => {
      if (rp.tournament_round) rounds.add(rp.tournament_round);
    });
    return Array.from(rounds).sort((a, b) => a - b);
  }, [roundPerformances]);

  useEffect(() => {
    if (availableRounds.length > 0 && selectedRound === 'total') {
      setSelectedRound(availableRounds[availableRounds.length - 1]);
    }
  }, [availableRounds]);

  const roundEntries = useMemo(() => {
    if (selectedRound === 'total') return [];
    const forRound = roundPerformances.filter(rp => rp.tournament_round === selectedRound);
    const bestByUser = new Map<string, RoundPerformance>();
    forRound.forEach(rp => {
      const existing = bestByUser.get(rp.user_id);
      if (!existing) {
        bestByUser.set(rp.user_id, rp);
      } else {
        const newScore = getPerformanceScore(rp) ?? -1;
        const oldScore = getPerformanceScore(existing) ?? -1;
        if (newScore > oldScore) bestByUser.set(rp.user_id, rp);
      }
    });
    return Array.from(bestByUser.values()).sort((a, b) => {
      const aScore = getPerformanceScore(a);
      const bScore = getPerformanceScore(b);
      if (aScore === null && bScore === null) return 0;
      if (aScore === null) return 1;
      if (bScore === null) return -1;
      return bScore - aScore;
    });
  }, [selectedRound, roundPerformances]);

  const getRoundLabel = (round: number) => {
    if (totalRounds === 0) return `Round ${round}`;
    if (round === totalRounds) return 'Final';
    if (round === totalRounds - 1) return 'Semi-Final';
    if (round === totalRounds - 2) return 'Quarter-Final';
    return `Round ${round}`;
  };

  const showRoundTabs = availableRounds.length > 0;

  if (entries.length === 0 && roundPerformances.length === 0) {
    return (
      <div
        className="rounded-lg p-8 text-center mx-4 my-4"
        style={{ backgroundColor: `${bgColor}50` }}
      >
        <div
          className="inline-block p-4 rounded-full mb-4"
          style={{ backgroundColor: `${bgColor}80` }}
        >
          <Mic size={32} className="text-white/50" />
        </div>
        <h4 className="text-lg font-semibold mb-2 text-white">No Entries Yet</h4>
        <p className="text-white/70 mb-4 max-w-md mx-auto">
          Be the first to enter this tournament and submit your performance!
        </p>
        {!isSharedView && (
          <Link
            to="/tournaments"
            className="inline-block px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: accentColor }}
          >
            Browse Tournaments
          </Link>
        )}
      </div>
    );
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Crown className="text-yellow-400" size={24} />;
      case 2: return <Medal className="text-gray-300" size={24} />;
      case 3: return <Award className="text-amber-600" size={24} />;
      default: return null;
    }
  };

  const getRankColors = (rank: number) => {
    switch (rank) {
      case 1:
        return { border: 'border-yellow-400/50', glow: 'bg-yellow-400/30', badge: 'bg-yellow-400 text-yellow-900', score: 'text-yellow-400', button: 'bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400' };
      case 2:
        return { border: 'border-gray-300/50', glow: 'bg-gray-300/30', badge: 'bg-gray-300 text-gray-800', score: 'text-gray-300', button: 'bg-gray-300/20 hover:bg-gray-300/30 text-gray-300' };
      case 3:
        return { border: 'border-amber-600/50', glow: 'bg-amber-600/30', badge: 'bg-amber-600 text-white', score: 'text-amber-500', button: 'bg-amber-600/20 hover:bg-amber-600/30 text-amber-500' };
      default:
        return { border: 'border-white/20', glow: 'bg-white/10', badge: 'bg-white/20 text-white', score: 'text-white', button: 'bg-white/20 hover:bg-white/30 text-white' };
    }
  };

  const handlePlayRoundPerformance = (rp: RoundPerformance) => {
    const perf = Array.isArray(rp.performance) ? rp.performance[0] : null;
    if (!perf) return;
    const syntheticEntry: Entry = {
      id: rp.id,
      user_id: rp.user_id,
      tournament_id: '',
      performance_id: perf.id,
      status: 'submitted',
      score: getPerformanceScore(rp),
      created_at: rp.created_at,
      updated_at: null,
      profile: rp.profile ? { username: rp.profile.username, full_name: rp.profile.full_name, avatar_url: rp.profile.avatar_url } : undefined,
      performance: {
        id: perf.id,
        vocal_recording_url: perf.vocal_recording_url,
        score: perf.score,
        created_at: perf.created_at,
        recording: {
          id: rp.id,
          tournament_round: rp.tournament_round,
          song: rp.song ? { id: rp.song.id, title: rp.song.title, artist: rp.song.artist, cover_url: rp.song.cover_url } : undefined,
        }
      }
    };
    onPlayPerformance?.(syntheticEntry);
  };

  return (
    <div className="space-y-4 p-4">
      {selectedRound === 'total' && (
        <>
          <TotalLeaderboard
            sortedEntries={sortedEntries}
            currentUserId={currentUserId}
            onPlayPerformance={onPlayPerformance}
            accentColor={accentColor}
            bgColor={bgColor}
            isSharedView={isSharedView}
            getRankIcon={getRankIcon}
            getRankColors={getRankColors}
          />
        </>
      )}

      {selectedRound !== 'total' && (
        <RoundLeaderboard
          roundEntries={roundEntries}
          roundNumber={selectedRound}
          roundLabel={getRoundLabel(selectedRound)}
          currentUserId={currentUserId}
          onPlayPerformance={handlePlayRoundPerformance}
          accentColor={accentColor}
          bgColor={bgColor}
          isSharedView={isSharedView}
        />
      )}

      {showRoundTabs && (
        <div className="mt-6 border-t border-white/10 pt-4">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedRound('total')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedRound === 'total'
                  ? 'text-white shadow-lg'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
              style={selectedRound === 'total' ? { backgroundColor: accentColor } : undefined}
            >
              <Trophy size={14} className="inline mr-1.5 -mt-0.5" />
              Total
            </button>
            {availableRounds.map(round => (
              <button
                key={round}
                onClick={() => setSelectedRound(round)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedRound === round
                    ? 'text-white shadow-lg'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
                style={selectedRound === round ? { backgroundColor: accentColor } : undefined}
              >
                {getRoundLabel(round)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface TotalLeaderboardProps {
  sortedEntries: Entry[];
  currentUserId?: string;
  onPlayPerformance?: (entry: Entry) => void;
  accentColor: string;
  bgColor: string;
  isSharedView: boolean;
  getRankIcon: (rank: number) => React.ReactNode;
  getRankColors: (rank: number) => { border: string; glow: string; badge: string; score: string; button: string };
}

const TotalLeaderboard: React.FC<TotalLeaderboardProps> = ({
  sortedEntries, currentUserId, onPlayPerformance, accentColor, bgColor, isSharedView, getRankIcon, getRankColors
}) => {
  if (sortedEntries.length === 0) {
    return (
      <div className="rounded-lg p-6 text-center" style={{ backgroundColor: `${bgColor}50` }}>
        <p className="text-white/70">No scored entries yet. Submit your performance to be the first on the leaderboard!</p>
      </div>
    );
  }

  return (
    <>
      {sortedEntries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {sortedEntries.slice(0, 3).map((entry, index) => {
            const rank = index + 1;
            const colors = getRankColors(rank);
            return (
              <div
                key={entry.id}
                className={`relative rounded-xl p-6 border-2 transition-all duration-300 hover:scale-[1.02] ${colors.border}`}
                style={{ backgroundColor: `${bgColor}60` }}
              >
                <div className="absolute top-4 right-4">{getRankIcon(rank)}</div>
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                    <div className={`absolute -inset-1 rounded-full ${colors.glow} blur`} />
                    {entry.profile?.avatar_url ? (
                      <img src={entry.profile.avatar_url} alt={entry.profile?.full_name || entry.profile?.username || 'User'} className="relative w-20 h-20 rounded-full object-cover border-4 border-white/20" />
                    ) : (
                      <div className="relative w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white border-4 border-white/20" style={{ backgroundColor: `${bgColor}80` }}>
                        {entry.profile?.username?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${colors.badge}`}>
                      #{rank}
                    </div>
                  </div>
                  <h3 className="font-bold text-lg mb-1 text-white">{entry.profile?.full_name || entry.profile?.username || 'Unknown'}</h3>
                  <p className="text-white/60 text-sm mb-3">@{entry.profile?.username || 'unknown'}</p>
                  {entry.performance?.recording?.tournament_round != null && (
                    <div className="mb-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${accentColor}30`, color: accentColor }}>
                        Round {entry.performance.recording.tournament_round}
                      </span>
                    </div>
                  )}
                  {entry.performance?.recording?.song && (
                    <div className="flex items-center gap-2 mb-3">
                      <img src={entry.performance.recording.song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=40&h=40&fit=crop'} alt={entry.performance.recording.song.title} className="w-10 h-10 rounded object-cover" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-white truncate max-w-[150px]">{entry.performance.recording.song.title}</p>
                        <p className="text-xs text-white/60 truncate max-w-[150px]">{entry.performance.recording.song.artist}</p>
                      </div>
                    </div>
                  )}
                  <div className={`text-3xl font-bold mb-3 ${colors.score}`}>{entry.score?.toFixed(1) || 'N/A'}</div>
                  {entry.performance_id && (
                    <button onClick={() => onPlayPerformance?.(entry)} className={`w-full py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${colors.button}`}>
                      <Play size={16} />
                      Play Recording
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h3 className="font-semibold mb-4 text-white" style={{ color: accentColor }}>Leaderboard</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `1px solid ${accentColor}30` }}>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Rank</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Participant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Round</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Song</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Submission Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Score</th>
              {!isSharedView && (
                <th className="px-4 py-3 text-right text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry, index) => (
              <tr
                key={entry.id}
                className="transition-colors"
                style={{
                  borderBottom: `1px solid ${accentColor}15`,
                  backgroundColor: entry.user_id === currentUserId ? `${accentColor}15` : 'transparent'
                }}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  {index === 0 ? (
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-500 font-bold">1</div>
                  ) : index === 1 ? (
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-400/20 text-gray-400 font-bold">2</div>
                  ) : index === 2 ? (
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-700/20 text-amber-700 font-bold">3</div>
                  ) : (
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white/70 font-bold" style={{ backgroundColor: `${bgColor}60` }}>{index + 1}</div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full overflow-hidden mr-3" style={{ backgroundColor: `${bgColor}80` }}>
                      {entry.profile?.avatar_url ? (
                        <img src={entry.profile.avatar_url} alt={entry.profile?.username || 'User'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-bold text-white">{entry.profile?.username?.[0]?.toUpperCase() || '?'}</div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-white">
                        {entry.profile?.username || 'Anonymous User'}
                        {entry.user_id === currentUserId && <span className="ml-2 text-sm" style={{ color: accentColor }}>(You)</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {entry.performance?.recording?.tournament_round != null ? (
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${accentColor}25`, color: accentColor }}>
                      Round {entry.performance.recording.tournament_round}
                    </span>
                  ) : (
                    <span className="text-white/30 text-xs">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  {entry.performance?.recording?.song ? (
                    <div className="flex items-center gap-2">
                      <img src={entry.performance.recording.song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=32&h=32&fit=crop'} alt={entry.performance.recording.song.title} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate max-w-[160px]">{entry.performance.recording.song.title}</p>
                        <p className="text-xs text-white/50 truncate max-w-[160px]">{entry.performance.recording.song.artist}</p>
                      </div>
                    </div>
                  ) : (
                    <span className="text-white/30 text-xs">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {entry.status === 'submitted' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-900/20 text-green-400">Submitted</span>
                  ) : entry.status === 'evaluated' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-900/20 text-blue-400">Evaluated</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-900/20 text-yellow-400">Registered</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-white/70">
                  {new Date(entry.performance?.created_at || entry.updated_at || entry.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Activity size={16} style={{ color: accentColor }} className="mr-1" />
                    <span className="font-semibold text-white">{entry.score || 'N/A'}</span>
                  </div>
                </td>
                {!isSharedView && (
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    {entry.performance_id && (
                      <button
                        className="p-2 rounded-lg text-white/70 hover:text-white transition-colors"
                        style={{ backgroundColor: `${bgColor}80` }}
                        onClick={() => onPlayPerformance?.(entry)}
                        title="Play performance"
                      >
                        <Play size={16} fill="currentColor" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

interface RoundLeaderboardProps {
  roundEntries: RoundPerformance[];
  roundNumber: number;
  roundLabel: string;
  currentUserId?: string;
  onPlayPerformance: (rp: RoundPerformance) => void;
  accentColor: string;
  bgColor: string;
  isSharedView: boolean;
}

const RoundLeaderboard: React.FC<RoundLeaderboardProps> = ({
  roundEntries, roundNumber, roundLabel, currentUserId, onPlayPerformance, accentColor, bgColor, isSharedView
}) => {
  if (roundEntries.length === 0) {
    return (
      <div className="rounded-lg p-8 text-center" style={{ backgroundColor: `${bgColor}50` }}>
        <Mic size={28} className="mx-auto mb-3 text-white/40" />
        <p className="text-white/70">No submissions for {roundLabel} yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-semibold mb-4 text-white" style={{ color: accentColor }}>
        {roundLabel} Leaderboard
      </h3>
      <div className="space-y-2">
        {roundEntries.map((rp, index) => {
          const perf = Array.isArray(rp.performance) ? rp.performance[0] : null;
          const score = getPerformanceScore(rp);
          const rank = index + 1;
          const isCurrentUser = rp.user_id === currentUserId;

          return (
            <div
              key={rp.id}
              className="flex items-center gap-3 rounded-lg px-4 py-3 transition-colors"
              style={{
                backgroundColor: isCurrentUser ? `${accentColor}15` : `${bgColor}30`,
                border: `1px solid ${isCurrentUser ? accentColor + '40' : 'transparent'}`
              }}
            >
              <div className="flex-shrink-0 w-8 text-center">
                {rank === 1 ? (
                  <Crown size={18} className="text-yellow-400 mx-auto" />
                ) : rank === 2 ? (
                  <Medal size={18} className="text-gray-300 mx-auto" />
                ) : rank === 3 ? (
                  <Award size={18} className="text-amber-600 mx-auto" />
                ) : (
                  <span className="text-sm font-bold text-white/50">{rank}</span>
                )}
              </div>

              <div className="h-9 w-9 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: `${bgColor}80` }}>
                {rp.profile?.avatar_url ? (
                  <img src={rp.profile.avatar_url} alt={rp.profile?.username || 'User'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-bold text-white text-sm">
                    {rp.profile?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-white text-sm truncate">
                  {rp.profile?.full_name || rp.profile?.username || 'Unknown'}
                  {isCurrentUser && <span className="ml-1.5 text-xs" style={{ color: accentColor }}>(You)</span>}
                </div>
                {rp.song && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {rp.song.cover_url && (
                      <img src={rp.song.cover_url} alt="" className="w-4 h-4 rounded object-cover" />
                    )}
                    <span className="text-xs text-white/50 truncate">
                      {rp.song.title} - {rp.song.artist}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="flex items-center gap-1">
                  <Activity size={14} style={{ color: accentColor }} />
                  <span className="font-bold text-white text-sm">{score ?? 'N/A'}</span>
                </div>
                {!isSharedView && perf && (
                  <button
                    className="p-1.5 rounded-lg text-white/60 hover:text-white transition-colors"
                    style={{ backgroundColor: `${bgColor}60` }}
                    onClick={() => onPlayPerformance(rp)}
                    title="Play performance"
                  >
                    <Play size={14} fill="currentColor" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentLeaderboard;
